import { difference, isEmpty } from "lodash"
import { fetchBootcampByKey } from "@/api/bootcamp.api"
import { BOOTCAMP_ENROLL_STEPS } from "@/constants/bootcamp"
import moment from "moment-timezone"
import router from "@/router"
import { fetchBootcamps, fetchFeaturedBootcamps } from "./bootcamp-enroll.apis"
import { ROUTER_NAMES } from "@/constants/router.constants.js"
import {
  bootcampMetaByKey,
  bootcampTemplateByKey,
  bootcampPartnerByKey,
  bootcampHeroByKey,
  bootcampInfoByKey,
  bootcampIntrosByKey,
  bootcampPreviewByKey,
  bootcampCoachByKey,
  afterBootcampByKey,
  bootcampBenefitsByKey,
  bootcampPriceByKey,
} from "./bootcamp-enroll.utils"

const state = {
  enrollStep: BOOTCAMP_ENROLL_STEPS.LANDING,
  enrollWithMembership: false,
  bootcamp: null,
  liveAndUpcomingBootcamps: [],
  featuredBootcamp: null,
  endedBootcamps: [],
  endedBootcampsCurrentPage: 0,
  endedBootcampsTotalPages: 2,
  loadedGames: [],
}
const getters = {
  enrollStep: state => state.enrollStep,
  enrollWithMembership: state => state.enrollWithMembership,
  bootcamp: state => state.bootcamp,
  bootcampMetaByKey: () => key => bootcampMetaByKey(key),
  bootcampTemplateByKey: () => key => bootcampTemplateByKey(key),
  bootcampPartnerByKey: () => key => bootcampPartnerByKey(key),
  bootcampHeroByKey: () => key => bootcampHeroByKey(key),
  bootcampInfoByKey: () => key => bootcampInfoByKey(key),
  bootcampIntrosByKey: () => key => bootcampIntrosByKey(key),
  bootcampPreviewByKey: () => key => bootcampPreviewByKey(key),
  bootcampCoachByKey: () => key => bootcampCoachByKey(key),
  afterBootcampByKey: () => key => afterBootcampByKey(key),
  bootcampBenefitsByKey: () => key => bootcampBenefitsByKey(key),
  bootcampPriceByKey: () => key => bootcampPriceByKey(key),
  userEnrolled: state => {
    try {
      return state.bootcamp.enrolled_at
    } catch (error) {
      return false
    }
  },
  bootcampSeats: state => {
    try {
      const stock = state.bootcamp.product.stock
      return (
        stock ||
        state.bootcamp.enrollment_group.seats -
          state.bootcamp.enrollment_group.seats_taken
      )
    } catch (error) {
      // static seats
      return 0
    }
  },
  bootcampProduct: state => {
    try {
      return state.bootcamp.product
    } catch (error) {
      return null
    }
  },
  bootcampPrice: (state, getters) => {
    try {
      return getters.bootcampProduct.current_price
    } catch (error) {
      return null
    }
  },
  bootcampOpen: (state, getters, rootState) => {
    try {
      const now = rootState.Util.Time.now
      return moment.duration(getters.bootcampEndTime.diff(now)).asSeconds() > 0
    } catch (error) {
      return false
    }
  },
  bootcampEndTime: state => {
    const endTime = state.bootcamp?.enrollment_group?.enrollment_ended_at
    if (router.currentRoute.name === ROUTER_NAMES.BOOTCAMP_ENROLL_STATIC) {
      const key = router.currentRoute.path.replace("/", "")
      return moment.utc(bootcampHeroByKey(key).enrollEndTime).local()
    }
    try {
      return moment.tz(endTime.date, endTime.timezone).local()
    } catch (error) {
      const key = router.currentRoute.path.replace("/", "")
      return moment.utc(bootcampHeroByKey(key).enrollEndTime).local()
    }
  },
  liveAndUpcomingBootcamps: state => state.liveAndUpcomingBootcamps,
  endedBootcamps: state => state.endedBootcamps,
  endedBootcampsCurrentPage: state => state.endedBootcampsCurrentPage,
  endedBootcampsTotalPages: state => state.endedBootcampsTotalPages,
  featuredBootcamp: state => state.featuredBootcamp,
  daysDiff: (state, getters, rootState, rootGetters) => {
    return Math.floor(
      moment
        .duration(getters.bootcampEndTime.diff(rootGetters["Util/Time/now"]))
        .asDays()
    )
  },
  hoursDiff (state, getters, rootState, rootGetters) {
    const hours = Math.floor(
      moment
        .duration(getters.bootcampEndTime.diff(rootGetters["Util/Time/now"]))
        .asHours()
    )
    return hours % 24
  },
  minutesDiff (state, getters, rootState, rootGetters) {
    const minutes = Math.floor(
      moment
        .duration(getters.bootcampEndTime.diff(rootGetters["Util/Time/now"]))
        .asMinutes()
    )
    return minutes % 60
  },
  secondsDiff (state, getters, rootState, rootGetters) {
    const seconds = Math.floor(
      moment
        .duration(getters.bootcampEndTime.diff(rootGetters["Util/Time/now"]))
        .asSeconds()
    )
    return seconds % 60
  },
}
const mutations = {
  setEnrollStep: (state, step) => (state.enrollStep = step),
  setEnrollWithMembership: (state, bol) => (state.enrollWithMembership = bol),
  setBootCamp: (state, bootcamp) => (state.bootcamp = bootcamp),
  setLiveAndUpcomingBootcamps: (state, bootcamps) =>
    (state.liveAndUpcomingBootcamps = bootcamps),
  setEndedBootcamps: (state, bootcamps) => (state.endedBootcamps = bootcamps),
  setLoadedGames: (state, loadedGames) => (state.loadedGames = loadedGames),
  // setLiveAndUpcomingBootcampsCurrentPage: (state, page) =>
  //   (state.liveAndUpcomingBootcampsCurrentPage = page),
  // setLiveAndUpcomingBootcampsTotalPages: (state, pages) =>
  //   (state.liveAndUpcomingBootcampsTotalPages = pages),
  setEndedBootcampsCurrentPage: (state, page) =>
    (state.endedBootcampsCurrentPage = page),
  setEndedBootcampsTotalPages: (state, pages) =>
    (state.endedBootcampsTotalPages = pages),
  setFeaturedBootcamp: (state, bootcamp) => (state.featuredBootcamp = bootcamp),
}
const actions = {
  async fetchBootcampByKey ({ commit }, key) {
    const bootcamp = await fetchBootcampByKey(key)
    await commit("setBootCamp", bootcamp)
  },
  async fetchLiveAndUpcomingBootcamps ({ commit, state, dispatch }, { games }) {
    const gamesToLoad = difference(games || [], state.loadedGames)
    if (isEmpty(gamesToLoad) && state.liveAndUpcomingBootcamps.length) return
    const filters = ["LIVE_AND_UPCOMING"]
    const data = await fetchBootcamps({
      games,
      filters,
      sortOption: "OLDEST_FIRST",
    })
    await dispatch("fetchFeaturedBootcamp")
    commit("setLiveAndUpcomingBootcamps", data.data)
    commit("setLoadedGames", games)
  },
  // async fetchMoreLiveAndEndedBootcamps ({ commit, state }) {
  //   if (state.liveAndUpcomingBootcamps.length) return
  //   const filters = ["LIVE_AND_UPCOMING"]
  //   const data = await fetchBootcamps({ filters, page })
  //   commit("setLiveAndUpcomingBootcamps", data.data)
  //   commit("setLiveAndUpcomingBootcampsCurrentPage", data.page)
  // },
  async fetchEndedBootcamps ({ commit, state }, { games }) {
    const gamesToLoad = difference(games || [], state.loadedGames)
    if (isEmpty(gamesToLoad) && state.endedBootcamps.length) return
    const filters = ["ENDED"]
    const data = await fetchBootcamps({
      games,
      filters,
    })
    commit(
      "setEndedBootcamps",
      data.data.map(bootcamp => ({ ...bootcamp, ended: true }))
    )
    commit("setEndedBootcampsCurrentPage", data.page)
    commit("setEndedBootcampsTotalPages", data.num_pages)
    commit("setLoadedGames", games)
  },
  async fetchMoreEndedBootcamps ({ commit, state }) {
    const bootcamps = state.endedBootcamps
    if (state.endedBootcampsCurrentPage + 1 >= state.endedBootcampsTotalPages) {
      return
    }
    const filters = ["ENDED"]
    const data = await fetchBootcamps({
      filters,
      page: state.endedBootcampsCurrentPage + 1,
    })
    commit("setEndedBootcamps", [
      ...bootcamps,
      ...data.data.map(bootcamp => ({ ...bootcamp, ended: true })),
    ])
    commit("setEndedBootcampsCurrentPage", data.page)
    commit("setEndedBootcampsTotalPages", data.num_pages)
  },
  async fetchFeaturedBootcamp ({ commit }) {
    const data = await fetchFeaturedBootcamps()
    const bootcamps = data.data
    if (!bootcamps?.length) return
    commit("setFeaturedBootcamp", bootcamps[0])
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/bootcamp-enroll/bootcamp-enroll.module.js